import { useMemo, useState, useRef, useEffect } from "react";
import type { FilterToggle } from "../../shared-components/fhir-sections/fhir-section";
import { TableRow } from "../utils/tables-metadata";

export type UseTableFiltersResult = {
  activeFilters: FilterToggle[];
  setActiveFilters: (filters: FilterToggle[]) => void;
  filteredRows: TableRow[];
};

/**
 * Hook for filtering table rows based on active filters.
 * Uses inverted filtering logic: all filters enabled by default, unchecking excludes rows.
 * Grouped strategy: AND between groups, OR within groups (for exclusion).
 *
 * @param rows - Table rows to filter
 * @param filterOptions - Initial filter options (all enabled by default)
 */
export function useTableFilters(
  rows: TableRow[],
  filterOptions: FilterToggle[] = []
): UseTableFiltersResult {
  const [activeFilters, setActiveFilters] = useState<FilterToggle[]>(filterOptions);
  const previousFilterOptionsRef = useRef<FilterToggle[]>([]);

  // Sync activeFilters when filterOptions change (e.g., when rows update)
  useEffect(() => {
    const filterOptionsChanged =
      JSON.stringify(previousFilterOptionsRef.current.map(f => f.label)) !==
      JSON.stringify(filterOptions.map(f => f.label));

    if (filterOptionsChanged && filterOptions.length > 0) {
      setActiveFilters(filterOptions);
      previousFilterOptionsRef.current = filterOptions;
    }
  }, [filterOptions]);

  const filteredRows = useMemo(() => {
    // Group all filters by their group label
    const filtersByGroup = activeFilters.reduce((acc, filter) => {
      const group = filter.groupLabel ?? "default";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group]?.push(filter);
      return acc;
    }, {} as Record<string, FilterToggle[]>);

    // Show a row only if it matches at least one checked filter in EACH group (AND between groups, OR within group)
    const filtered = rows.filter(row => {
      return Object.values(filtersByGroup).every((groupFilters: FilterToggle[]) => {
        // Get filters that are checked (enabled/active)
        const checkedFilters = groupFilters.filter(f => f.isChecked);

        // Row must match at least one checked filter in this group to be shown
        // If no filters are checked in this group, no rows pass (returns false)
        return checkedFilters.some((filter: FilterToggle) => filter.filterFn(row));
      });
    });

    return filtered;
  }, [rows, activeFilters]);

  return { activeFilters, setActiveFilters, filteredRows };
}
